import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { facilitatorAPI, reviewAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { TrustBadge } from '../components/TrustBadge';
import ReviewForm from '../components/ReviewForm';
import { ShareButton } from '../components/ShareButton';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  MapPin, 
  Calendar, 
  Mail, 
  Phone, 
  ArrowLeft,
  Star,
  Languages,
  Briefcase,
  Users,
  Shield,
  ShieldCheck,
  Heart,
  Droplets,
  CheckCircle2,
  Info,
  CalendarCheck,
  Flag,
  Award,
  Eye
} from 'lucide-react';
import { formatDate, firstAidLevels, campTypes, reviewerRoles } from '../lib/utils';

// Helper to format verification date as "Month Year"
const formatVerificationDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
};

const FacilitatorDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [facilitator, setFacilitator] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReviewId, setDisputeReviewId] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);

  // Check for preview mode
  const searchParams = new URLSearchParams(window.location.search);
  const isPreviewMode = searchParams.get('preview') === 'true';

  // Check if current user owns this profile
  const isOwner = user && facilitator && facilitator.user_id === user.id;

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [facResponse, reviewsResponse] = await Promise.all([
        facilitatorAPI.get(id),
        reviewAPI.list({ target_type: 'facilitator', target_id: id })
      ]);
      setFacilitator(facResponse.data);
      setReviews(reviewsResponse.data);
    } catch (err) {
      setError('Failed to load facilitator profile.');
      console.error('Error:', err);
    }
    setLoading(false);
  };

  const handleDisputeReview = async () => {
    if (!disputeReviewId || !disputeReason.trim()) return;
    
    setDisputeSubmitting(true);
    try {
      await reviewAPI.dispute(disputeReviewId, { reason: disputeReason });
      setDisputeOpen(false);
      setDisputeReason('');
      setDisputeReviewId(null);
      // Refresh reviews
      const reviewsResponse = await reviewAPI.list({ target_type: 'facilitator', target_id: id });
      setReviews(reviewsResponse.data);
    } catch (err) {
      console.error('Dispute error:', err);
    }
    setDisputeSubmitting(false);
  };

  // Helper to get reviewer role label
  const getReviewerRoleLabel = (role) => {
    const found = reviewerRoles.find(r => r.value === role);
    return found ? found.label : role;
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-20 md:pb-0">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <Skeleton className="aspect-square rounded-3xl" />
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !facilitator) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Facilitator not found'}</p>
          <Button asChild>
            <Link to="/directory/facilitators">Back to Directory</Link>
          </Button>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="bg-primary text-primary-foreground py-3 px-6 text-center" data-testid="preview-banner">
          <div className="container mx-auto flex items-center justify-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-medium">This is a preview of your public profile</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.close()}
              className="ml-4"
            >
              Close Preview
            </Button>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Back button */}
        <Link 
          to="/directory/facilitators"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Facilitators
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Photo & Quick Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-card rounded-3xl border border-border overflow-hidden">
                <div className="aspect-square">
                  <img
                    src={facilitator.profile_photo || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600'}
                    alt={facilitator.full_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 space-y-4">
                  <TrustBadge status={facilitator.verification_status} size="large" />
                  
                  {averageRating && (
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold">{averageRating}</span>
                      <span className="text-muted-foreground">({reviews.length} reviews)</span>
                    </div>
                  )}

                  {/* Contact buttons */}
                  <div className="space-y-2 pt-4 border-t border-border">
                    {facilitator.contact_email && (
                      <Button 
                        asChild 
                        className="w-full rounded-full"
                        data-testid="contact-email-btn"
                      >
                        <a href={`mailto:${facilitator.contact_email}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </a>
                      </Button>
                    )}
                    {facilitator.contact_phone && (
                      <Button 
                        asChild 
                        variant="outline" 
                        className="w-full rounded-full"
                        data-testid="contact-phone-btn"
                      >
                        <a href={`tel:${facilitator.contact_phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header: Name, location, experience, availability */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground">
                  {facilitator.full_name}
                </h1>
                <ShareButton type="facilitators" id={facilitator.id} title={`${facilitator.full_name} — Campia Facilitator`} className="shrink-0 mt-1" />
              </div>
              <div className="flex flex-wrap gap-4 text-muted-foreground mb-3">
                {facilitator.provinces?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {facilitator.provinces.join(', ')}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {facilitator.years_experience} years experience
                </span>
              </div>
              {/* Available for bookings */}
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                facilitator.available_for_bookings === true
                  ? 'bg-primary/10 text-primary'
                  : facilitator.available_for_bookings === false
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-muted/50 text-muted-foreground'
              }`}>
                <CalendarCheck className={`w-4 h-4 ${
                  facilitator.available_for_bookings === true ? 'text-primary' : ''
                }`} />
                {facilitator.available_for_bookings === true
                  ? 'Available'
                  : facilitator.available_for_bookings === false
                  ? 'Not available'
                  : 'Availability not indicated'
                }
              </div>
            </div>

            {/* Qualifications & Safety - PRIORITISED */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-semibold text-xl">Qualifications & Safety</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {/* First Aid */}
                <div className={`p-4 rounded-xl text-center ${
                  facilitator.first_aid_certified 
                    ? 'bg-primary/10 border border-primary/20' 
                    : 'bg-muted/50'
                }`}>
                  <Heart className={`w-5 h-5 mx-auto mb-2 ${
                    facilitator.first_aid_certified ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className="font-medium text-sm">First Aid</p>
                  <p className={`text-xs mt-1 ${
                    facilitator.first_aid_certified ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {facilitator.first_aid_certified 
                      ? `Certified${facilitator.first_aid_level ? ` (${firstAidLevels.find(l => l.value === facilitator.first_aid_level)?.label || facilitator.first_aid_level})` : ''}`
                      : 'Not certified'
                    }
                  </p>
                </div>

                {/* Police Clearance with Tooltip */}
                <TooltipProvider delayDuration={0}>
                  <div className={`p-4 rounded-xl text-center relative ${
                    facilitator.police_clearance === 'verified'
                      ? 'bg-primary/10 border border-primary/20'
                      : facilitator.police_clearance === 'pending'
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-muted/50'
                  }`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          type="button"
                          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-muted/50 transition-colors"
                          aria-label="More info about police clearance"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px] text-xs bg-foreground text-background">
                        <p>Verified by Campia indicates documentation has been reviewed. Campia does not guarantee ongoing status.</p>
                      </TooltipContent>
                    </Tooltip>
                    <ShieldCheck className={`w-5 h-5 mx-auto mb-2 ${
                      facilitator.police_clearance === 'verified' 
                        ? 'text-primary' 
                        : facilitator.police_clearance === 'pending'
                        ? 'text-amber-600'
                        : 'text-muted-foreground'
                    }`} />
                    <p className="font-medium text-sm">Police Clearance</p>
                    <p className={`text-xs mt-1 ${
                      facilitator.police_clearance === 'verified' 
                        ? 'text-primary' 
                        : facilitator.police_clearance === 'pending'
                        ? 'text-amber-600'
                        : 'text-muted-foreground'
                    }`}>
                      {facilitator.police_clearance === 'verified' 
                        ? 'Verified by Campia' 
                        : facilitator.police_clearance === 'pending'
                        ? 'Pending'
                        : 'Not provided'
                      }
                    </p>
                  </div>
                </TooltipProvider>

                {/* ID Verified */}
                <div className={`p-4 rounded-xl text-center ${
                  facilitator.id_verified 
                    ? 'bg-primary/10 border border-primary/20' 
                    : 'bg-muted/50'
                }`}>
                  <CheckCircle2 className={`w-5 h-5 mx-auto mb-2 ${
                    facilitator.id_verified ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className="font-medium text-sm">ID</p>
                  <p className={`text-xs mt-1 ${
                    facilitator.id_verified ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {facilitator.id_verified ? 'Verified' : 'Not verified'}
                  </p>
                </div>

                {/* Swimming Ability */}
                <div className={`p-4 rounded-xl text-center ${
                  facilitator.swimming_ability === 'can_swim'
                    ? 'bg-blue-50 border border-blue-200' 
                    : facilitator.swimming_ability === 'cannot_swim'
                    ? 'bg-muted/50'
                    : 'bg-muted/50'
                }`}>
                  <Droplets className={`w-5 h-5 mx-auto mb-2 ${
                    facilitator.swimming_ability === 'can_swim' ? 'text-blue-500' : 'text-muted-foreground'
                  }`} />
                  <p className="font-medium text-sm">Swimming</p>
                  <p className={`text-xs mt-1 ${
                    facilitator.swimming_ability === 'can_swim' ? 'text-blue-600' : 'text-muted-foreground'
                  }`}>
                    {facilitator.swimming_ability === 'can_swim' 
                      ? 'Can swim' 
                      : facilitator.swimming_ability === 'cannot_swim'
                      ? 'Cannot swim'
                      : 'Not indicated'
                    }
                  </p>
                </div>
              </div>

              {/* Verified by Campia badge with date */}
              {facilitator.verification_status === 'verified' && (
                <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 px-4 py-2 rounded-full w-fit">
                  <ShieldCheck className="w-4 h-4" />
                  <span>
                    Verified by Campia
                    {facilitator.verification_date && (
                      <span className="text-primary/70"> · {formatVerificationDate(facilitator.verification_date)}</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Specialties - Second priority */}
            {facilitator.specialties?.length > 0 && (
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-heading font-semibold text-xl mb-4">Specialties</h2>
                <div className="flex flex-wrap gap-2">
                  {facilitator.specialties.map((specialty) => (
                    <span 
                      key={specialty}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* About - Third priority */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <h2 className="font-heading font-semibold text-xl mb-4">About</h2>
              <p className="text-muted-foreground leading-relaxed">
                {facilitator.bio?.replace('[DEMO] ', '')}
              </p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Camp Roles */}
              {facilitator.camp_roles?.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase className="w-5 h-5 text-primary" />
                    <h3 className="font-heading font-semibold">Camp Roles</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facilitator.camp_roles.map((role) => (
                      <span 
                        key={role}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm capitalize"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Camp Types */}
              {facilitator.camp_types?.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-secondary" />
                    <h3 className="font-heading font-semibold">Camp Types</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facilitator.camp_types.map((type) => (
                      <span 
                        key={type}
                        className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm capitalize"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Age Groups */}
              {facilitator.age_groups?.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-primary" />
                    <h3 className="font-heading font-semibold">Age Groups</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facilitator.age_groups.map((age) => (
                      <span 
                        key={age}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                      >
                        {age}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {facilitator.languages?.length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Languages className="w-5 h-5 text-primary" />
                    <h3 className="font-heading font-semibold">Languages</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facilitator.languages.map((lang) => (
                      <span 
                        key={lang}
                        className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Reviews Section */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-xl">Reviews</h2>
                {/* Show review form for logged-in reviewers who don't own this profile (hidden in preview mode) */}
                {!isPreviewMode && user && !isOwner && (user.role === 'reviewer' || user.role === 'camp') && (
                  <ReviewForm
                    targetType="facilitator"
                    targetId={id}
                    targetName={facilitator.full_name}
                    userRole={user.role}
                    onSuccess={loadData}
                  />
                )}
              </div>
              
              {reviews.length > 0 ? (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-border pb-6 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{review.reviewer_name}</span>
                          {review.reviewer_role && (
                            <span className="text-muted-foreground text-sm ml-2">
                              — {getReviewerRoleLabel(review.reviewer_role)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-muted-foreground mb-3">
                        {review.review_text?.replace('[DEMO] ', '')}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review.created_at)}
                        </span>
                        {/* Dispute button - only visible to profile owner (hidden in preview mode) */}
                        {!isPreviewMode && isOwner && !review.disputed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              setDisputeReviewId(review.id);
                              setDisputeOpen(true);
                            }}
                          >
                            <Flag className="w-3 h-3 mr-1" />
                            Dispute
                          </Button>
                        )}
                        {review.disputed && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            Dispute pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No reviews yet. Be the first to share your experience!
                </p>
              )}
            </div>

            {/* Dispute Review Dialog */}
            <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dispute Review</DialogTitle>
                  <DialogDescription>
                    Explain why you believe this review is inaccurate or unfair. Your dispute will be reviewed by our admin team.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  placeholder="Reason for dispute (max 300 characters)"
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value.slice(0, 300))}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {disputeReason.length}/300 characters
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDisputeOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDisputeReview}
                    disabled={!disputeReason.trim() || disputeSubmitting}
                  >
                    {disputeSubmitting ? 'Submitting...' : 'Submit Dispute'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Last Updated */}
            <p className="text-sm text-muted-foreground">
              Profile last updated: {formatDate(facilitator.last_updated)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitatorDetail;
